import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';

const initialState = {
  groupTypes: null,
  selectedGroupType: null,
  isFetching: false,

  deleteRequestPending: false,
  deleteResultCode: null,
  deleteWhichId: null
};

const slice = createSlice({
  name: 'groupType',
  initialState,
  reducers: {
    deleteGroupType(state, action) {
      const { groupTypeId } = action.payload;

      if (state.groupTypes) {
        state.groupTypes = state.groupTypes.filter(
          gt => gt.id !== groupTypeId
        );
      }
    },
    getGroupTypes(state, action) {
      const { groupTypes } = action.payload;

      state.groupTypes = groupTypes;
    },
    setGroupTypes(state, action) {
      const { groupTypes } = action.payload;

      state.groupTypes = groupTypes;
    },
    setSelectedGroupType(state, action) {
      const { groupType } = action.payload;

      state.selectedGroupType = groupType;
    },
    setFetching(state, action) {
      const { isFetching } = action.payload;

      state.isFetching = isFetching;
    },
    deleteStart(state, action) {
      state.deleteWhichId = action.payload;
      state.deleteRequestPending = true;
    },
    deleteRequestEnd(state) {
      state.deleteRequestPending = false;
    },
    setDeleteResultCode(state, action) {
      state.deleteResultCode = action.payload;
    },
    deleteCleanup(state) {
      state.deleteWhichId = null;
      state.deleteResultCode = null;
    }
  }
});

export const { reducer } = slice;

export const getGroupTypes = contestId => async dispatch => {
  const query = `
    {
      contest{
        groupTypes {
          id,
          name,
          minParticipants,
          maxParticipants,
          allowUserGroupCreation,
          allowUserChildGroupCreation,
          allowChildUsers,
          description,
          groups {
              id,
              name
          }
          parentGroupTypeId
        }
      }
    }
  `;

  dispatch(slice.actions.setFetching({ isFetching: true }));

  const response = await axios.post(`/admin/${contestId}/g`, { query });

  dispatch(slice.actions.setFetching({ isFetching: false }));

  dispatch(slice.actions.getGroupTypes(response.data.data.contest));
};

export const setSelectedGroupType = groupType => async dispatch => {
  dispatch(slice.actions.setSelectedGroupType({ groupType }));
};

export const clearGroupTypes = () => async dispatch => {
  dispatch(slice.actions.setGroupTypes({ groupTypes: null }));
};

// to delete a group type using deleteGroupType
// first, make sure no other group type is currently being deleted
// by e.g. seeing if deleteWhichId is set
// then call await dispatch(deleteGroupType(...)) with arguments:
// - groupTypeId: the ID of the group type to delete
// - contestId: the ID of the contest the group type is part of
// - fetchCtl: optional Axios CancelToken source
// either the deletion is successful and the function returns 'SUCCESS'
// or it fails, and throws an Error e with a status code stored in e.message:
// - CANCEL: the request was cancelled with the CancelToken
// - HTTP-[HTTP status]: server responded with unexpected HTTP status
//   or an error with no message property
//   in particular: HTTP-404 when the group type does not exist
// - IN_USE: there are groups of this group type, so it could not be deleted
// - NO_AUTH: not authorized to delete this group type
// - NO_MATCH: the group type exists, but not in the given contest
// - NO_RESPONSE: the request was sent, but there was no response
// - PARENT: the group type is parent group type and could not be deleted
// - UNKNOWN: other error, not handled
// a component can follow how progress through variables in the store:
// - deleteRequestPending is true while waiting for the network request
//   and false otherwise
// - deleteResultCode is null while waiting for the network request,
//   and contains the result code afterwards
// - deleteWhichId contains the ID of the group type while it is deleted
//   and otherwise contains null
// finally, dispatch deleteGroupTypeCleanup to reset the state
export const deleteGroupType = (groupTypeId, contestId, fetchCtl = null) =>
  async dispatch => {
    dispatch(slice.actions.deleteStart(groupTypeId));
    try {
      await axios.delete(
        `/admin/${contestId}/group_types/${groupTypeId}`,
        {
          cancelToken: fetchCtl?.token,
          validateStatus: status => status === 204
        }
      );
      dispatch(slice.actions.deleteGroupType({ groupTypeId }));
      dispatch(slice.actions.setDeleteResultCode('SUCCESS'));
      return 'SUCCESS';
    } catch (error) {
      const errorObject = new Error();
      errorObject.caughtError = error;
      if (error?.response) {
        // unexpected response received
        // only happens if status < 400 or if the response has no 'message'
        dispatch(slice.actions.setDeleteResultCode(
          `HTTP-${error.response.status}`
        ));
        errorObject.message = `HTTP-${error.response.status}`;
      } else if (error?.request) {
        // no response; typically network error
        dispatch(slice.actions.setDeleteResultCode('NO_RESPONSE'));
        errorObject.message = 'NO_RESPONSE';
      } else if (error?.message === 'Group type has child group types.') {
        dispatch(slice.actions.setDeleteResultCode('PARENT'));
        errorObject.message = 'PARENT';
      } else if (error?.message === 'Group type has groups.') {
        dispatch(slice.actions.setDeleteResultCode('IN_USE'));
        errorObject.message = 'IN_USE';
      } else if (error?.message === 'canceled') {
        dispatch(slice.actions.setDeleteResultCode('CANCEL'));
        errorObject.message = 'CANCEL';
      } else if (error?.message === 'no_match') {
        // group type exists but is part of different contest
        dispatch(slice.actions.setDeleteResultCode('NO_MATCH'));
        errorObject.message = 'NO_MATCH';
      } else if (error?.message === 'Unauthorized') {
        dispatch(slice.actions.setDeleteResultCode('NO_AUTH'));
        errorObject.message = 'NO_AUTH';
      } else {
        // unknown error
        dispatch(slice.actions.setDeleteResultCode('UNKNOWN'));
        errorObject.message = 'UNKNOWN';
      }
      throw errorObject;
    } finally {
      dispatch(slice.actions.deleteRequestEnd());
    }
  };

export const deleteGroupTypeCleanup = () => dispatch => {
  dispatch(slice.actions.deleteCleanup());
};

export default slice;
